import React, { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Loader from "../components/Loader";

const INV_COL = "INVOICES";

export default function GSTFiling() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    // Data structures for GST Summary
    const [summary, setSummary] = useState({
        totalTaxable: 0,
        totalCGST: 0,
        totalSGST: 0,
        totalIGST: 0,
        totalInvoiceValue: 0,
        totalInvoicesCount: 0
    });

    const [filteredInvoices, setFilteredInvoices] = useState([]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, INV_COL), orderBy("date", "desc"));
            const snap = await getDocs(q);
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Exclude cancelled invoices
            const validInvoices = data.filter(inv => inv.status !== "CANCELLED");
            setInvoices(validInvoices);
        } catch (err) {
            console.error("Error fetching invoices for GST:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, []);

    const parseDate = (d) => {
        if (!d) return new Date();
        if (d.toDate && typeof d.toDate === 'function') return d.toDate();
        if (d.seconds) return new Date(d.seconds * 1000);
        const parsed = new Date(d);
        if (!isNaN(parsed.getTime())) return parsed;
        const parts = String(d).split(/[-/]/);
        if (parts.length === 3) {
            if (parts[0].length === 4) return new Date(parts[0], parts[1] - 1, parts[2]);
            return new Date(parts[2], parts[1] - 1, parts[0]);
        }
        return new Date();
    };

    useEffect(() => {
        if (!invoices.length && !loading) return;

        let tTaxable = 0;
        let tCGST = 0;
        let tSGST = 0;
        let tIGST = 0;
        let tValue = 0;

        const filtered = invoices.filter(inv => {
            const dStr = inv.date;
            const dObj = parseDate(dStr);
            const m = dObj.getMonth() + 1;
            const y = dObj.getFullYear();
            return m === Number(selectedMonth) && y === Number(selectedYear);
        });

        filtered.forEach(inv => {
            const taxable = Number(inv.subtotal) || 0;
            const totalTax = Number(inv.taxAmount) || 0;
            const isInter = inv.isInterState;
            
            tTaxable += taxable;
            tValue += Number(inv.total) || 0;

            if (isInter) {
                tIGST += totalTax;
            } else {
                tCGST += totalTax / 2;
                tSGST += totalTax / 2;
            }
        });

        setSummary({
            totalTaxable: tTaxable,
            totalCGST: tCGST,
            totalSGST: tSGST,
            totalIGST: tIGST,
            totalInvoiceValue: tValue,
            totalInvoicesCount: filtered.length
        });
        
        setFilteredInvoices(filtered);

    }, [invoices, selectedMonth, selectedYear, loading]);

    const handleExportExcel = () => {
        if (filteredInvoices.length === 0) {
            alert("No data to export for the selected month/year.");
            return;
        }

        const data = filteredInvoices.map(doc => {
            const isInter = doc.isInterState;
            const taxable = Number(doc.subtotal) || 0;
            const totalTax = Number(doc.taxAmount) || 0;
            let cgst = 0, sgst = 0, igst = 0;
            
            if (isInter) {
                igst = totalTax;
            } else {
                cgst = totalTax / 2;
                sgst = totalTax / 2;
            }

            const docDate = parseDate(doc.date);
            const formattedDate = `${docDate.getDate().toString().padStart(2, '0')}-${(docDate.getMonth() + 1).toString().padStart(2, '0')}-${docDate.getFullYear()}`;

            return {
                "Invoice Number": doc.docNumber || "N/A",
                "Invoice Date": formattedDate,
                "Customer Name": doc.customerDetails?.name || "N/A",
                "Customer GSTIN": doc.customerDetails?.gstin || "URP",
                "Place of Supply": doc.customerDetails?.state || "N/A",
                "Taxable Value": taxable.toFixed(2),
                "CGST Amount": cgst.toFixed(2),
                "SGST Amount": sgst.toFixed(2),
                "IGST Amount": igst.toFixed(2),
                "Total Invoice Value": (Number(doc.total) || 0).toFixed(2),
            };
        });

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "GST_Report");
        
        // Add Summary Info at the bottom using Array of Arrays for precise alignment
        XLSX.utils.sheet_add_aoa(ws, [
            ["", "", "", "", "", "", "", "", "", ""],
            [
                "TOTALS", "", "", "", "", 
                summary.totalTaxable.toFixed(2), 
                summary.totalCGST.toFixed(2), 
                summary.totalSGST.toFixed(2), 
                summary.totalIGST.toFixed(2), 
                summary.totalInvoiceValue.toFixed(2)
            ]
        ], { origin: -1 });

        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        
        // Use native anchor tag approach to guarantee file name and extension
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `GST_Report_${selectedMonth}_${selectedYear}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading) return <Loader />;

    return (
        <div className="bg-white p-4 rounded-4 shadow-sm border mt-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold mb-0 text-primary">GST Filing Report</h4>
                <button onClick={handleExportExcel} className="btn btn-primary d-flex align-items-center gap-2" style={{ borderRadius: "20px" }}>
                    <i className="bi bi-file-earmark-excel"></i> Export to Excel
                </button>
            </div>

            <div className="row mb-4 align-items-center bg-light p-3 rounded-4 mx-0">
                <div className="col-md-3">
                    <label className="fw-bold small mb-1">Select Month</label>
                    <select
                        className="form-select"
                        style={{ borderRadius: "15px" }}
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                        {Array.from({ length: 12 }).map((_, i) => (
                            <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
                        ))}
                    </select>
                </div>
                <div className="col-md-3">
                    <label className="fw-bold small mb-1">Select Year</label>
                    <select
                        className="form-select"
                        style={{ borderRadius: "15px" }}
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                    >
                        {Array.from({ length: 5 }).map((_, i) => {
                            const y = new Date().getFullYear() - i;
                            return <option key={y} value={y}>{y}</option>
                        })}
                    </select>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-md-3 mb-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-light">
                        <div className="card-body">
                            <p className="text-secondary mb-1 small fw-bold text-uppercase">Total Invoices</p>
                            <h3 className="mb-0 fw-bold">{summary.totalInvoicesCount}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-primary bg-opacity-10">
                        <div className="card-body">
                            <p className="text-primary mb-1 small fw-bold text-uppercase">Taxable Value</p>
                            <h3 className="mb-0 fw-bold">₹{summary.totalTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-info bg-opacity-10">
                        <div className="card-body">
                            <p className="text-info mb-1 small fw-bold text-uppercase">Total CGST & SGST</p>
                            <h3 className="mb-0 fw-bold text-info">₹{(summary.totalCGST + summary.totalSGST).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                            <div className="small text-secondary mt-1">
                                CGST: ₹{summary.totalCGST.toLocaleString('en-IN')} | SGST: ₹{summary.totalSGST.toLocaleString('en-IN')}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-warning bg-opacity-10">
                        <div className="card-body">
                            <p className="text-warning mb-1 small fw-bold text-uppercase">Total IGST</p>
                            <h3 className="mb-0 fw-bold text-warning">₹{summary.totalIGST.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
                        </div>
                    </div>
                </div>
            </div>

            <div className="table-responsive rounded-4 border">
                <table className="table table-hover table-borderless mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th className="py-3 px-4">Date</th>
                            <th className="py-3 px-4">Invoice No</th>
                            <th className="py-3 px-4">Customer</th>
                            <th className="py-3 px-4 text-end">Taxable Value</th>
                            <th className="py-3 px-4 text-end">CGST</th>
                            <th className="py-3 px-4 text-end">SGST</th>
                            <th className="py-3 px-4 text-end">IGST</th>
                            <th className="py-3 px-4 text-end">Total Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredInvoices.length > 0 ? (
                            filteredInvoices.map((inv) => {
                                const isInter = inv.isInterState;
                                const taxable = Number(inv.subtotal) || 0;
                                const totalTax = Number(inv.taxAmount) || 0;
                                let cgst = 0, sgst = 0, igst = 0;
                                
                                if (isInter) {
                                    igst = totalTax;
                                } else {
                                    cgst = totalTax / 2;
                                    sgst = totalTax / 2;
                                }

                                const parsedDate = parseDate(inv.date);
                                const dateStr = `${parsedDate.getDate().toString().padStart(2, '0')}-${(parsedDate.getMonth() + 1).toString().padStart(2, '0')}-${parsedDate.getFullYear()}`;

                                return (
                                    <tr key={inv.id} className="border-bottom">
                                        <td className="px-4 py-3 text-secondary text-nowrap">{dateStr}</td>
                                        <td className="px-4 py-3 fw-bold">{inv.docNumber}</td>
                                        <td className="px-4 py-3">
                                            <div className="fw-bold">{inv.customerDetails?.name || "N/A"}</div>
                                            <div className="small text-secondary text-truncate" style={{ maxWidth: '200px' }}>
                                                GSTIN: {inv.customerDetails?.gstin || "URP"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-end">₹{taxable.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-end">{cgst > 0 ? `₹${cgst.toFixed(2)}` : '-'}</td>
                                        <td className="px-4 py-3 text-end">{sgst > 0 ? `₹${sgst.toFixed(2)}` : '-'}</td>
                                        <td className="px-4 py-3 text-end">{igst > 0 ? `₹${igst.toFixed(2)}` : '-'}</td>
                                        <td className="px-4 py-3 text-end fw-bold">₹{(Number(inv.total) || 0).toFixed(2)}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center py-5 text-secondary">
                                    <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                                    <h5>No Invoices Found</h5>
                                    <p className="mb-0">There are no invoices for the selected month and year.</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
